'use client';

import { ElementType, FC, useState } from 'react';

import { DynamicPageContentType } from '@ravago/shared/page-data/models/contentful';
import { NormalizedNavigationItem } from '@ravago/shared/page-data/models/contentful-mapped';
import { Customer } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Flyout, FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';

import { CustomerSwitchButton } from '../../../shared/customer-switch-button/customer-switch-button';
import { CustomerSwitchDialog } from '../../../shared/customer-switch-dialog/customer-switch-dialog';
import { LogoutButton } from '../../../shared/logout-button/logout-button';
import { MenuItem } from '../../../shared/menu-item/menu-item';

interface Props {
   locale: string;
   navigationItems: NormalizedNavigationItem[];
   currentCustomer?: Customer;
   showCustomerSwitch?: boolean;
   showLogout?: boolean;
   linkComponent?: ElementType;
   dynamicPageUrls?: { [key in DynamicPageContentType]?: string };
}

export const Menu: FC<Props> = ({ locale, navigationItems, currentCustomer, showCustomerSwitch, showLogout, linkComponent, dynamicPageUrls }) => {
   const [menuOpen, setMenuOpen] = useState(false);
   const [customerSwitchOpen, setCustomerSwitchOpen] = useState(false);

   const { tabLand, tabPort } = useBreakpoint();
   const isMobile = tabLand || tabPort;

   return (
      <>
         <Box gap="sm" spacing="none" direction="column">
            {isMobile && <IconButton ariaLabel="open" icon={menuOpen ? 'angles-left' : 'angles-right'} onClick={() => setMenuOpen(true)} />}
            <MenuItem
               locale={locale}
               navigationItems={navigationItems ?? []}
               hideLabel={isMobile}
               linkComponent={linkComponent}
               variant="alt"
               cyLabel="dynamic-menu-item"
               dynamicPageUrls={dynamicPageUrls}
            />
            {(showCustomerSwitch || showLogout) && <Divider />}
            {showCustomerSwitch && (
               <CustomerSwitchButton
                  currentCustomer={currentCustomer}
                  hideLabel={isMobile}
                  cyLabel="dynamic-menu-customer-switch"
                  onClick={() => setCustomerSwitchOpen(true)}
               />
            )}
            {showLogout && <LogoutButton hideLabel={isMobile} cyLabel="dynamic-menu-logout" />}
         </Box>
         {showCustomerSwitch && currentCustomer && customerSwitchOpen && (
            <CustomerSwitchDialog open={customerSwitchOpen} currentCustomer={currentCustomer} onDialogClose={() => setCustomerSwitchOpen(false)} />
         )}

         <Flyout ariaLabel="menu" position="left" width="400px" open={menuOpen} closeOnOutsideClick onFlyoutClose={() => setMenuOpen(false)}>
            <FlyoutHeader />
            <FlyoutContent>
               <MenuItem
                  locale={locale}
                  navigationItems={navigationItems ?? []}
                  linkComponent={linkComponent}
                  variant="alt"
                  cyLabel="dynamic-menu-item"
                  dynamicPageUrls={dynamicPageUrls}
               />
            </FlyoutContent>
            <FlyoutActions>
               {showCustomerSwitch && (
                  <CustomerSwitchButton
                     currentCustomer={currentCustomer}
                     cyLabel="dynamic-menu-customer-switch"
                     onClick={() => setCustomerSwitchOpen(true)}
                  />
               )}
               {showLogout && <LogoutButton cyLabel="dynamic-menu-logout" />}
            </FlyoutActions>
         </Flyout>
      </>
   );
};
